<script setup lang="ts">
import type { FieldRule } from '#layers/access';
import type { GlobalCollectionId } from '#layers/data-model';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface PolicyFieldRuleFilterButtonProps {
	collectionId: GlobalCollectionId;
	followItemRules: boolean;
	mode: 'follow-item-rules' | 'custom-field-rules';
}

const props = defineProps<PolicyFieldRuleFilterButtonProps>();
const emit = defineEmits<{
	followItemRulesChange: [follow: boolean];
}>();

const modelValue = defineModel<FieldRule>({ required: true });

const { t } = useI18n();

const color = computed(() => props.mode === 'follow-item-rules' ? 'neutral' : 'warning');
const icon = computed(() => props.mode === 'follow-item-rules' ? 'lucide:filter' : 'lucide:triangle-alert');
const buttonClass = computed(() => props.mode === 'follow-item-rules' ? 'text-dimmed' : '');
const tooltipText = computed(() => props.mode === 'follow-item-rules' ? t('policy.field_rules.filter_follow_item_rules') : t('policy.field_rules.filter_custom_field_rules'));

// TODO can this not be a defineModel?
const followRef = ref(props.followItemRules);
const follow = computed({
	get() {
		return followRef.value;
	},
	set(newFollow: boolean) {
		followRef.value = newFollow;

		emit('followItemRulesChange', newFollow);
	},
});

watch(() => props.followItemRules, (newFollowItemRules) => {
	followRef.value = newFollowItemRules;
});
</script>

<template>
	<DPopover v-if="modelValue" mode="click" :content="{ side: 'left' }" :ui="{ content: 'min-w-72' }">
		<DTooltip :text="tooltipText">
			<DButton :class="buttonClass" :icon :color variant="link" size="sm" />
		</DTooltip>

		<template #content>
			<DAnimatedSheet>
				<div class="border-b px-4 pt-4 pb-2">
					<DSwitch
						v-model="follow"
						size="sm"
						:label="t('policy.field_rules.follow_item_rules')"
						:description="t('policy.field_rules.follow_item_rules_description')"
					/>
				</div>

				<InputFilterBlock
					v-model="modelValue.filter"
					:collection-id
					:disabled="follow"
					clear-filter-display-style="button"
					hide-border
				/>
			</DAnimatedSheet>
		</template>
	</DPopover>
</template>
